import React, { useEffect, useRef, useState } from "react";
import { signIn, confirmSignIn, fetchAuthSession } from "@aws-amplify/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { configureAuth } from "../../auth/authConfig";
import { isAdminConfigured, isAdminSession, safeAdminReturn } from "../../auth/adminAuthConfig";
import { withSessionLock } from "../../auth/sessionQueue";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(null);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const lock = useRef(false);
  const alive = useRef(true);
  const nav = useNavigate();
  const [params] = useSearchParams();
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function submit(event) {
    event.preventDefault();
    if (lock.current || !isAdminConfigured()) return;
    lock.current = true; setBusy(true); setError("");
    try {
      const complete = await withSessionLock(async () => {
        configureAuth("admin");
        try {
          if (step?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" && newPassword !== repeatPassword) throw new Error("PasswordMismatch");
          const result = step
            ? await confirmSignIn({ challengeResponse: step.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" ? newPassword : code.trim() })
            : await signIn({ username: email.trim().toLowerCase(), password });
          if (!result.isSignedIn) {
            const next = result.nextStep;
            if (!["CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED", "CONFIRM_SIGN_IN_WITH_TOTP_CODE", "CONTINUE_SIGN_IN_WITH_TOTP_SETUP"].includes(next?.signInStep)) throw new Error("AdditionalStep");
            if (alive.current) setStep(next);
            return false;
          }
        } catch (ex) {
          if (ex.name !== "UserAlreadyAuthenticatedException") throw ex;
        }
        if (!isAdminSession(await fetchAuthSession())) throw new Error("Denied");
        return true;
      });
      if (complete && alive.current) nav(safeAdminReturn(params.get("returnTo")), { replace: true });
    } catch (ex) {
      if (alive.current) setError(ex.message === "PasswordMismatch" ? "Your new passwords do not match."
        : ex.name === "InvalidPasswordException" ? "Use at least 12 characters, including uppercase, lowercase, a number, and a symbol."
        : ["CodeMismatchException", "EnableSoftwareTokenMFAException"].includes(ex.name) ? "That verification code did not match. Try the current code from your authenticator app."
        : ex.message === "AdditionalStep"
        ? "Your account requires another verification step. Contact your administrator to complete access."
        : ["TooManyRequestsException", "LimitExceededException"].includes(ex.name)
          ? "Too many attempts. Please wait before trying again."
          : "We couldn't sign you in. Check your admin credentials and try again.");
    } finally {
      lock.current = false;
      if (alive.current) { setBusy(false); setPassword(""); setCode(""); setNewPassword(""); setRepeatPassword(""); }
    }
  }
  const inputClass = "w-full min-h-[44px] rounded-lg border border-[#3F256B]/25 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3F256B]";
  return <main className="min-h-[70vh] bg-[#FBF7F0] px-4 py-12 text-[#3F256B]">
    <div className="mx-auto max-w-md rounded-2xl border border-[#3F256B]/15 bg-white p-6 sm:p-8 shadow-sm">
      <BookOpen aria-hidden="true" className="mb-4" size={32} />
      <p className="text-sm mb-2">Behind the shelves</p>
      <h1 className="font-serif text-3xl mb-3">Admin Login</h1>
      <p className="mb-6">Sign in to care for the Bookish Circle community.</p>
      {!isAdminConfigured() && <p role="status" className="mb-4">Admin sign-in is currently unavailable. Please contact your administrator.</p>}
      <form aria-label="Admin sign in" onSubmit={submit} aria-busy={busy}>
        <fieldset disabled={busy || !isAdminConfigured()}>
          {!step && <><label htmlFor="admin-email" className="block mb-2">Email address</label>
          <input id="admin-email" type="email" autoComplete="username" required maxLength={254} className={inputClass} value={email} onChange={e => setEmail(e.target.value)} />
          <label htmlFor="admin-password" className="block mt-4 mb-2">Password</label>
          <input id="admin-password" type={showPassword ? "text" : "password"} autoComplete="current-password" required className={inputClass} value={password} onChange={e => setPassword(e.target.value)} />
          <button type="button" aria-pressed={showPassword} className="min-h-[44px] underline" onClick={() => setShowPassword(v => !v)}>{showPassword ? "Hide password" : "Show password"}</button></>}
          {step?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" && <>
            <p role="status" className="mb-4">Choose your own password to finish setting up your admin account.</p>
            <p id="password-rules" className="text-sm mb-4">Use at least 12 characters, including uppercase, lowercase, a number, and a symbol.</p>
            <label htmlFor="admin-new-password">New password</label>
            <input id="admin-new-password" type="password" autoComplete="new-password" aria-describedby="password-rules" required minLength={12} className={inputClass} value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            <label htmlFor="admin-repeat-password" className="block mt-4">Repeat new password</label>
            <input id="admin-repeat-password" type="password" autoComplete="new-password" required minLength={12} className={inputClass} value={repeatPassword} onChange={e => setRepeatPassword(e.target.value)} />
          </>}
          {step && step.signInStep !== "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" && <>
            {step.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" && <div className="mb-4">
              <p role="status">Add Bookish Circle Admin to your authenticator app using this setup key. Keep this key private.</p>
              <code className="block break-all my-3 select-all">{step.totpSetupDetails?.sharedSecret}</code>
            </div>}
            <label htmlFor="admin-code">Authenticator code</label>
            <input id="admin-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required className={inputClass} value={code} onChange={e => setCode(e.target.value)} />
          </>}
          {step && <button type="button" className="min-h-[44px] underline mt-3" onClick={() => { setStep(null); setCode(""); setNewPassword(""); setRepeatPassword(""); setError(""); }}>Start over</button>}
          <button type="submit" className="mt-4 w-full min-h-[44px] rounded-lg bg-[#3F256B] text-white px-4 py-2 disabled:opacity-60">{busy ? "Signing in..." : step ? "Continue" : "Sign in"}</button>
        </fieldset>
        {error && <p role="alert" className="mt-4 text-red-700">{error}</p>}
      </form>
      <Link to="/" className="inline-block mt-6 py-2 underline">Back to Bookish Circle</Link>
    </div>
  </main>;
}
